<template>
  <div
    class="axd-card"
    :class="{ 'axd-card--unreachable': !camera.isCameraReachable }"
  >
    <!-- Header: identificador + botones -->
    <div class="axd-card__header">
      <div class="axd-card__id">
        <span class="axd-card__dot" :class="dotClass"></span>
        {{ camera.recorderIdentifier }}
      </div>
      <div class="axd-card__actions">
        <button
          class="axd-rules-btn"
          title="Gestionar reglas de retención"
          @click="showRetention = true"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
            <line
              x1="3"
              y1="9"
              x2="21"
              y2="9"
              stroke="currentColor"
              stroke-width="2"
            />
            <line
              x1="8"
              y1="2"
              x2="8"
              y2="6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="16"
              y1="2"
              x2="16"
              y2="6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <button
          class="axd-refresh-btn"
          title="Forzar refresco ahora"
          :disabled="refreshing"
          @click="$emit('refresh', camera.recorderIdentifier)"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            class="axd-refresh-btn__icon"
            :class="{ 'axd-refresh-btn__icon--spinning': refreshing }"
          >
            <path
              d="M23 4v6h-6M1 20v-6h6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
            <path
              d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Barra de uso de disco -->
    <div class="axd-card__bar-wrap">
      <div class="axd-card__bar-track">
        <div
          class="axd-card__bar-fill"
          :class="barColorClass"
          :style="{ width: (usedPercent !== null ? usedPercent : 0) + '%' }"
        ></div>
      </div>
      <span v-if="usedPercent !== null" class="axd-card__bar-label">
        {{ usedPercent }}% usado
        <span v-if="totalSizeFormatted" class="axd-card__bar-total">
          · {{ totalSizeFormatted }}
        </span>
      </span>
      <span v-else class="axd-card__bar-label axd-card__bar-label--empty">
        Sin datos de espacio
      </span>
    </div>

    <!-- Badges de alerta -->
    <div class="axd-card__badges" v-if="hasAnyBadge">
      <span
        v-if="!camera.isCameraReachable"
        class="axd-badge axd-badge--warning"
        >No disponible</span
      >
      <span v-if="camera.isFull" class="axd-badge axd-badge--danger"
        >Disco lleno</span
      >
      <span
        v-if="camera.status && camera.status !== 'OK'"
        class="axd-badge axd-badge--danger"
        >Estado: {{ camera.status }}</span
      >
    </div>

    <!-- Footer -->
    <div class="axd-card__footer">Actualizado {{ formattedUpdatedAt }}</div>

    <!-- Diálogo de retención -->
    <AxisRetentionDialog
      :visible="showRetention"
      :recorder-identifier="camera.recorderIdentifier"
      @close="showRetention = false"
    />
  </div>
</template>

<script>
import AxisRetentionDialog from "./AxisRetentionDialog.vue";

export default {
  name: "AxisCameraCard",
  components: { AxisRetentionDialog },
  props: {
    camera: { type: Object, required: true },
    refreshing: { type: Boolean, default: false },
  },
  data() {
    return {
      showRetention: false,
    };
  },
  computed: {
    usedPercent() {
      const { totalSize, freeSize } = this.camera;
      if (!totalSize || freeSize === null || freeSize === undefined) {
        return null;
      }
      return Math.round(((totalSize - freeSize) / totalSize) * 100);
    },
    totalSizeFormatted() {
      if (!this.camera.totalSize) return null;
      const gb = (this.camera.totalSize / (1024 * 1024)).toFixed(0);
      return `${gb} GB`;
    },
    isAnomalous() {
      return (
        this.camera.isFull ||
        (this.camera.status && this.camera.status !== "OK")
      );
    },
    barColorClass() {
      if (this.isAnomalous) return "axd-card__bar-fill--danger";
      if (this.usedPercent === null) return "axd-card__bar-fill--unknown";
      if (this.usedPercent >= 90) return "axd-card__bar-fill--danger";
      if (this.usedPercent >= 70) return "axd-card__bar-fill--warning";
      return "axd-card__bar-fill--success";
    },
    dotClass() {
      return this.camera.isCameraReachable
        ? "axd-card__dot--online"
        : "axd-card__dot--offline";
    },
    hasAnyBadge() {
      return (
        !this.camera.isCameraReachable ||
        this.camera.isFull ||
        (this.camera.status && this.camera.status !== "OK")
      );
    },
    formattedUpdatedAt() {
      if (!this.camera.updatedAt) return "—";
      const date = new Date(this.camera.updatedAt);
      const diffMs = Date.now() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "hace un momento";
      if (diffMin < 60) return `hace ${diffMin} min`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `hace ${diffH} h`;
      const diffD = Math.floor(diffH / 24);
      return `hace ${diffD} d`;
    },
  },
};
</script>

<style scoped>
.axd-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.axd-card--unreachable {
  border-color: #fde68a;
  background: #fffbeb;
}

.axd-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.axd-card__id {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.axd-card__actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.axd-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.axd-card__dot--online {
  background: #22c55e;
}
.axd-card__dot--offline {
  background: #f59e0b;
}

.axd-rules-btn,
.axd-refresh-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, color 0.15s;
}
.axd-rules-btn:hover,
.axd-refresh-btn:hover:not(:disabled) {
  border-color: #0284c7;
  color: #0284c7;
}
.axd-refresh-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.axd-refresh-btn__icon--spinning {
  animation: axdSpin 0.9s linear infinite;
}
@keyframes axdSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.axd-card__bar-wrap {
  margin-bottom: 8px;
}

.axd-card__bar-track {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #f1f5f9;
  overflow: hidden;
  margin-bottom: 4px;
}

.axd-card__bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}
.axd-card__bar-fill--success {
  background: #22c55e;
}
.axd-card__bar-fill--warning {
  background: #f59e0b;
}
.axd-card__bar-fill--danger {
  background: #ef4444;
}
.axd-card__bar-fill--unknown {
  background: #cbd5e1;
}

.axd-card__bar-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}
.axd-card__bar-label--empty {
  font-style: italic;
}
.axd-card__bar-total {
  color: #94a3b8;
  font-weight: 400;
}

.axd-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 8px;
}

.axd-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
  border: 1px solid;
  white-space: nowrap;
}
.axd-badge--warning {
  background: #fffbeb;
  border-color: #fde68a;
  color: #b45309;
}
.axd-badge--danger {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #ef4444;
}

.axd-card__footer {
  font-size: 11px;
  color: #94a3b8;
}
</style>
